import { useLayoutEffect, useRef } from "react";
import type { ReactNode } from "react";
import { ArrowLeft } from "lucide-react";
import { AppSwitcher } from "./AppSwitcher";
import type { SwitcherUser } from "./AppSwitcher";
import { apps } from "../lib/apps";
import type { SwitcherDestination } from "../lib/apps";

export const APP_HEADER_TITLE_MIN_SIZE = 14;

export function calculateAppHeaderTitleSize(defaultSize: number, availableWidth: number, measure: (fontSize: number) => number, minimum = APP_HEADER_TITLE_MIN_SIZE) {
  if (measure(defaultSize) <= availableWidth + 1) return defaultSize;
  let low = minimum;
  let high = defaultSize;
  for (let attempt = 0; attempt < 8; attempt += 1) {
    const size = (low + high) / 2;
    if (measure(size) <= availableWidth + 1) low = size;
    else high = size;
  }
  return low;
}

function fitTitle(title: HTMLElement, header: HTMLElement, actions: HTMLElement | null) {
  title.style.removeProperty("font-size");
  const defaultSize = Number.parseFloat(getComputedStyle(title).fontSize);
  const headerRect = header.getBoundingClientRect();
  const titleRect = title.getBoundingClientRect();
  const actionRect = actions?.getBoundingClientRect();
  const availableWidth = Math.max(0, (actionRect?.left ?? headerRect.right) - titleRect.left - (actionRect ? 11 : 0));
  const size = calculateAppHeaderTitleSize(defaultSize, availableWidth, fontSize => {
    title.style.fontSize = `${fontSize}px`;
    return title.scrollWidth;
  });
  if (size === defaultSize) title.style.removeProperty("font-size");
  else title.style.fontSize = `${size}px`;
}

export interface AppHeaderProps {
  currentDestination: SwitcherDestination;
  user?: SwitcherUser;
  onSignOut?: () => Promise<void>;
  actions?: ReactNode;
  asHeading?: boolean;
  title?: string;
  backHref?: string;
  backLabel?: string;
  onBack?: () => void;
}

/** The shared, compact navigation row for every family app. */
export function AppHeader({ currentDestination, user, onSignOut, actions, asHeading = false, title, backHref, backLabel = "Back", onBack }: AppHeaderProps) {
  const headerTitle = title ?? apps[currentDestination].title;
  const header = useRef<HTMLElement>(null);
  const titleElement = useRef<HTMLElement>(null);
  const actionsElement = useRef<HTMLDivElement>(null);
  const setTitleElement = (element: HTMLElement | null) => { titleElement.current = element; };
  useLayoutEffect(() => {
    const headerNode = header.current;
    const titleNode = titleElement.current;
    if (!headerNode || !titleNode) return;
    let frame = 0;
    const measure = () => {
      frame = 0;
      fitTitle(titleNode, headerNode, actionsElement.current);
    };
    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(measure);
    };
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(schedule);
    observer?.observe(headerNode);
    if (actionsElement.current) observer?.observe(actionsElement.current);
    window.addEventListener("resize", schedule, { passive: true });
    window.visualViewport?.addEventListener("resize", schedule, { passive: true });
    document.fonts?.addEventListener("loadingdone", schedule);
    void document.fonts?.ready.then(schedule);
    measure();
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", schedule);
      window.visualViewport?.removeEventListener("resize", schedule);
      document.fonts?.removeEventListener("loadingdone", schedule);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [headerTitle, Boolean(actions)]);
  return (
    <header className="app-header" ref={header}>
      {onBack
        ? <button className="app-header-back" type="button" onClick={onBack} aria-label={backLabel}><ArrowLeft size={22} aria-hidden="true" /></button>
        : backHref
        ? <a className="app-header-back" href={backHref} aria-label={backLabel}><ArrowLeft size={22} aria-hidden="true" /></a>
        : <AppSwitcher currentDestination={currentDestination} user={user} onSignOut={onSignOut} />}
      {asHeading
        ? <h1 ref={setTitleElement} className="app-header-title">{headerTitle}</h1>
        : <p ref={setTitleElement} className="app-header-title">{headerTitle}</p>}
      {actions && <div ref={actionsElement} className="app-header-actions">{actions}</div>}
    </header>
  );
}
